// The brand's campaigns as a table, the way an admin panel lists work: what
// each one is, where it stands, and what is moving on it (creators picked,
// applicants waiting, videos approved, unread messages), newest change first.
// The campaigns page adds status filters and a search; the overview shows the
// latest few. On a phone each row becomes a card.

import {Link, useNavigate} from 'react-router-dom';
import type {BrandCampaign} from '../../shared/brand-api';
import {Icon} from '../creator/components/Icon';
import {CAMPAIGN_STATUS, HOLDING, rateLabel, workMode} from './format';

export interface CampaignStats {
  picked: number;
  /** Applicants still waiting for the brand; zero for a first-come campaign. */
  waiting: number;
  delivered: number;
  approved: number;
  /** Videos the campaign asks for: creators × videos each (a month's, when recurring). */
  target: number;
  unread: number;
}

export function campaignStats(campaign: BrandCampaign): CampaignStats {
  const people = campaign.participants;
  return {
    picked: people.filter((person) => HOLDING.has(person.reservation_status ?? '')).length,
    waiting: campaign.take_mode === 'apply' ? campaign.applicants.filter((applicant) => applicant.status === 'applied').length : 0,
    delivered: people.reduce((sum, person) => sum + person.deliverable_count, 0),
    approved: people.reduce((sum, person) => sum + person.approved_count, 0),
    target: campaign.creator_count * campaign.videos_per_creator,
    unread: people.reduce((sum, person) => sum + person.unread_count, 0),
  };
}

/** "just now", "3 h ago", "2 d ago", then the date. */
export function ago(value: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - Date.parse(value)) / 60_000));
  if (minutes < 2) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} d ago`;
  return new Date(value).toLocaleDateString(undefined, {day: 'numeric', month: 'short'});
}

function Meter({value, of}: {value: number; of: number}) {
  const share = of > 0 ? Math.min(1, value / of) : 0;
  return <span className="meter" aria-hidden="true"><i style={{width: `${Math.round(share * 100)}%`}} /></span>;
}

export function CampaignTable({campaigns, compact = false}: {campaigns: BrandCampaign[]; compact?: boolean}) {
  const navigate = useNavigate();
  return <div className={`table-wrap${compact ? ' compact' : ''}`}>
    <table className="data-table campaign-table">
      <thead><tr>
        <th scope="col">Campaign</th>
        <th scope="col">Status</th>
        <th scope="col">Creators</th>
        {compact ? null : <th scope="col">Applicants</th>}
        <th scope="col">Videos approved</th>
        <th scope="col" className="num col-updated">Updated</th>
      </tr></thead>
      <tbody>{campaigns.map((campaign) => {
        const stats = campaignStats(campaign);
        const to = `/campaigns/${campaign.id}`;
        return <tr key={campaign.id} onClick={(event) => { if (!(event.target as HTMLElement).closest('a')) navigate(to); }}>
          <td className="cell-main" data-label="Campaign">
            <Link to={to}><b>{campaign.title}</b></Link>
            <small>{workMode(campaign.delivery_mode, campaign.account_mode)} · {rateLabel(campaign.rate_cents, campaign.market_rates, campaign.audience_countries, campaign.currency)} {campaign.fee_on_top ? 'creator pay / video' : 'per video'}{campaign.cadence === 'recurring' ? ' · monthly' : ''}</small>
            {stats.unread ? <span className="cell-flag"><Icon name="chat" size={13} />{stats.unread} unread</span> : null}
          </td>
          <td data-label="Status"><span className={`status-pill ${campaign.status}`}>{CAMPAIGN_STATUS[campaign.status]}</span></td>
          <td data-label="Creators"><span className="cell-count"><b>{stats.picked}</b> / {campaign.creator_count}</span><Meter value={stats.picked} of={campaign.creator_count} /></td>
          {compact ? null : <td data-label="Applicants">{campaign.access_mode === 'invited'
            ? <span className="cell-muted">Invite only</span>
            : campaign.take_mode !== 'apply'
            ? <span className="cell-muted">First come</span>
            : stats.waiting ? <span className="cell-badge">{stats.waiting} waiting</span> : <span className="cell-muted">None waiting</span>}</td>}
          <td data-label="Videos approved"><span className="cell-count"><b>{stats.approved}</b> / {stats.target}{campaign.cadence === 'recurring' ? <small> a month</small> : null}</span><Meter value={stats.approved} of={stats.target} />
            {stats.delivered > stats.approved ? <small className="cell-sub">{stats.delivered - stats.approved} in review</small> : null}</td>
          <td className="num cell-muted col-updated" data-label="Updated">{ago(campaign.updated_at)}</td>
        </tr>;
      })}</tbody>
    </table>
  </div>;
}
